/**
 * Inline blob preview helpers (Office Web Viewer embed + PDF iframe).
 * Office fetches the file from Microsoft servers; SAS must allow that read or embed fails.
 */

const OFFICE_EMBED_BASE = "https://view.officeapps.live.com/op/embed.aspx";

/** @param {string} sasHttpsUrl Fresh read URL for the blob */
export function buildOfficeEmbedUrl(sasHttpsUrl) {
  return `${OFFICE_EMBED_BASE}?src=${encodeURIComponent(sasHttpsUrl)}`;
}

/** @param {string} fileLabel Basename or path hint for extension
 * @param {string} [blobUrl] Fallback: inspect pathname when label has no `.pdf`
 */
export function isLikelyPdfFile(fileLabel, blobUrl) {
  const l = String(fileLabel || "");
  if (/\.pdf$/i.test(l)) return true;
  try {
    const path = new URL(blobUrl || "").pathname;
    return /\.pdf(?:\?|$)/i.test(path) || /\.pdf$/i.test(path);
  } catch {
    return false;
  }
}

/** .docx templates: use server mammoth preview instead of Office Web Viewer iframe. */
export function isLikelyDocxFile(fileLabel, blobUrl) {
  const l = String(fileLabel || "");
  if (/\.docx$/i.test(l)) return true;
  try {
    const path = new URL(blobUrl || "").pathname;
    return /\.docx$/i.test(path);
  } catch {
    return false;
  }
}

/**
 * Iframe src for in-app preview: PDF uses direct URL; Office types use viewer embed.
 * @param {string} readableHttpsUrl SAS or public HTTPS URL
 * @param {string} [fileLabel]
 */
export function buildPreviewEmbedSrc(readableHttpsUrl, fileLabel, blobUrl) {
  if (!readableHttpsUrl) return "";
  if (isLikelyPdfFile(fileLabel, blobUrl)) return readableHttpsUrl;
  if (isLikelyDocxFile(fileLabel, blobUrl)) return "";
  return buildOfficeEmbedUrl(readableHttpsUrl);
}

/**
 * Fresh read URL from API (mirrors AppContext.openBlobUrl without opening a tab).
 * @param {string} apiBase e.g. apiConfig.baseUrl
 * @param {string} rawUrl Stored blob URL (may have expired SAS)
 * @param {() => Promise<string>} getAccessToken
 * @returns {Promise<string | null>}
 */
export async function resolveReadableBlobUrl(apiBase, rawUrl, getAccessToken) {
  if (!rawUrl) return null;
  try {
    const token = await getAccessToken();
    const res = await fetch(`${apiBase}/api/v1/blob-sas?url=${encodeURIComponent(rawUrl)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success && json.data?.url) return json.data.url;
    return rawUrl;
  } catch {
    return rawUrl;
  }
}
